import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { Video } from "@/features/studio-ai/types/studio-ai.types"

import { Palette, PenLine, Settings, SlidersHorizontal } from "@/components/icons"
import { RemixVideoSelectorModal } from "@/features/studio-ai/components/remix-video-selector-modal"
import { useGenerateVideo } from "@/features/studio-ai/hooks/use-generate-video"
import { MediaType } from "@/features/studio-ai/types/studio-ai.types"
import { getApiErrorMessage } from "@/lib/api-error-message"
import { useStudioAIStore } from "@/stores/studio-ai-store"

import { PromptStep } from "./prompt-step"
import { StyleStep } from "./style-step"
import { VideoAdvancedStep } from "./video-advanced-step"
import { VideoDurationStep } from "./video-duration-step"
import { VideoFormatStep } from "./video-format-step"
import { WizardContainer } from "./wizard-container"

type VideoWizardProps = {
  onComplete: (mediaType: MediaType) => void
  onCancel: () => void
}

export function VideoWizard({ onComplete, onCancel }: VideoWizardProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi" })
  const [currentStep, setCurrentStep] = useState(0)
  const [showRemixModal, setShowRemixModal] = useState(false)

  const wizardPrompt = useStudioAIStore((state) => state.wizardPrompt)
  const setWizardPrompt = useStudioAIStore((state) => state.setWizardPrompt)
  const settings = useStudioAIStore((state) => state.settings)
  const updateSettings = useStudioAIStore((state) => state.updateSettings)
  const videoReferenceImageUrl = useStudioAIStore((state) => state.videoReferenceImageUrl)
  const setVideoReferenceImageUrl = useStudioAIStore((state) => state.setVideoReferenceImageUrl)
  const remixVideoId = useStudioAIStore((state) => state.remixVideoId)
  const setRemixVideoId = useStudioAIStore((state) => state.setRemixVideoId)
  const selectedStyleTemplateId = useStudioAIStore((state) => state.selectedStyleTemplateId)
  const setSelectedStyleTemplateId = useStudioAIStore((state) => state.setSelectedStyleTemplateId)
  const selectedStyleAdherence = useStudioAIStore((state) => state.selectedStyleAdherence)
  const setSelectedStyleAdherence = useStudioAIStore((state) => state.setSelectedStyleAdherence)
  const generationState = useStudioAIStore((state) => state.generationState)
  const resetGenerationState = useStudioAIStore((state) => state.resetGenerationState)
  const { mutateAsync: generateVideo } = useGenerateVideo()

  useEffect(() => {
    resetGenerationState()
  }, [resetGenerationState])

  const handleRemixSelect = (video: Video) => {
    const videoId = video.job_id || video.id

    if (!videoId) {
      toast.error(t("videoSettings.failedToSelectVideo"), {
        description: t("videoSettings.failedToSelectVideoDesc"),
      })
      return
    }

    setRemixVideoId(videoId)

    const promptPreview = video.prompt ? video.prompt.substring(0, 50) : t("common.noPrompt")
    toast.success(t("videoSettings.videoSelectedForRemix"), {
      description: t("videoSettings.videoSelectedForRemixDesc", {
        prompt: promptPreview,
        id: videoId,
      }),
    })
  }

  const steps = useMemo(
    () => [
      { key: "prompt", label: t("wizard.steps.prompt"), icon: PenLine },
      { key: "duration-format", label: t("wizard.steps.durationFormat"), icon: SlidersHorizontal },
      { key: "style", label: t("wizard.steps.style"), icon: Palette },
      { key: "advanced", label: t("wizard.steps.advanced"), icon: Settings },
    ],
    [t]
  )

  const isNextDisabled = useMemo(() => {
    if (currentStep === 0) return wizardPrompt.trim().length === 0
    return false
  }, [currentStep, wizardPrompt])

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleStepClick = (step: number) => {
    if (step < currentStep) {
      setCurrentStep(step)
    }
  }

  const handleGenerate = async () => {
    try {
      await generateVideo({
        prompt: wizardPrompt,
        duration: settings.duration,
        size: settings.size,
        quality: settings.quality,
        style: settings.style,
        reference_image_url: videoReferenceImageUrl ?? undefined,
        remix_video_id: remixVideoId ?? undefined,
        style_template_id: selectedStyleTemplateId ?? undefined,
        style_adherence: selectedStyleTemplateId ? selectedStyleAdherence : undefined,
      })
      onComplete(MediaType.Video)
    } catch (error) {
      // useGenerateVideo's onError shows the backend error text for every
      // failure except a missing style template, which gets its own message
      // here. Read the JSON body, not `error.message` — Axios' message never
      // contains it.
      const message = getApiErrorMessage(error)
      if (message.toLowerCase().includes("style template not found or access denied")) {
        toast.error(t("video.style.templateUnavailable"))
      }
    }
  }

  const videoAspectRatio = settings.size === "720x1280" ? "9:16" : "16:9"

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <PromptStep
            aspectRatio={videoAspectRatio}
            duration={settings.duration}
            mediaType={MediaType.Video}
            prompt={wizardPrompt}
            size={settings.size}
            style={settings.style}
            onPromptChange={setWizardPrompt}
          />
        )
      case 1:
        return (
          <div className="mx-auto max-w-2xl space-y-8">
            <VideoDurationStep
              selected={settings.duration}
              onSelect={(duration) => updateSettings({ duration })}
            />
            <VideoFormatStep
              selected={settings.size}
              onSelect={(size) =>
                updateSettings({
                  size: size as "1280x720" | "720x1280",
                })
              }
            />
          </div>
        )
      case 2:
        return (
          <StyleStep
            mediaType={MediaType.Video}
            selected={settings.style}
            selectedAdherence={selectedStyleAdherence}
            selectedTemplateId={selectedStyleTemplateId}
            onAdherenceSelect={setSelectedStyleAdherence}
            onSelect={(style) => updateSettings({ style })}
            onTemplateSelect={(templateId: string | null) => {
              setSelectedStyleTemplateId(templateId)
            }}
          />
        )
      case 3:
        return (
          <VideoAdvancedStep
            referenceImageUrl={videoReferenceImageUrl}
            remixVideoId={remixVideoId}
            onReferenceImageChange={setVideoReferenceImageUrl}
            onRemixVideoChange={setRemixVideoId}
            onSelectRemixVideo={() => setShowRemixModal(true)}
          />
        )
      default:
        return null
    }
  }

  return (
    <>
      <WizardContainer
        currentStep={currentStep}
        isGenerating={generationState.isGenerating}
        isNextDisabled={isNextDisabled}
        steps={steps}
        title={t("video.title")}
        onBack={handleBack}
        onCancel={onCancel}
        onGenerate={() => void handleGenerate()}
        onNext={handleNext}
        onStepClick={handleStepClick}
      >
        {renderStep()}
      </WizardContainer>

      <RemixVideoSelectorModal
        open={showRemixModal}
        onOpenChange={setShowRemixModal}
        onSelect={handleRemixSelect}
      />
    </>
  )
}
